// AdminAuthServices.jsx
import { useState } from "react";
import API from "../../api/apiAdmin";
import { useNotification } from "../../context/NotificationContext";

export function useAdminAuth() {
  const [admin, setAdmin] = useState(JSON.parse(localStorage.getItem("admin")) || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { showNotification } = useNotification();

  const _extract = (res) => (res?.data ? res.data : res);

  // === ADMIN LOGIN ===
  const adminLogin = async (email, password, nextPath = null) => {
    setLoading(true);
    setError(null);
    try {
      const payload = { email, password };
      if (nextPath) payload.next = nextPath;

      const res = await API.post("/auth/admin/login", payload);
      const data = _extract(res);

      if (data.status === "failed") {
        setError(data.message);
        showNotification({
          type: "failed",
          title: "Login Failed",
          message: data.message,
        });
        return data;
      }

      if (data.access_token) {
        localStorage.setItem("admin_token", data.access_token);
      }

      if (data.admin) {
        localStorage.setItem("admin", JSON.stringify(data.admin));
        setAdmin(data.admin);
      }

      showNotification({
        type: "success",
        title: "Login Successful",
        message: `Welcome back, ${data.admin?.full_name || "Admin"}!`,
      });

      return data;
    } catch (err) {
      const msg = err.response?.data?.message || "Network error or server unavailable.";
      setError(msg);
      showNotification({
        type: "failed",
        title: "Login Failed",
        message: msg,
      });
      return { status: "failed", message: msg };
    } finally {
      setLoading(false);
    }
  };


  const adminLogout = async (navigate) => {
    try {
      await API.post("/auth/admin/logout");
    } catch {
      console.warn("⚠️ Admin logout backend call failed silently");
    } finally {
      localStorage.removeItem("admin");
      localStorage.removeItem("admin_token");
      setAdmin(null);
      if (navigate) navigate("/admin/login");
    }
  };



  const validateInviteAPI = async (token) => {
    try {
      const response = await API.get(`/auth/admin/validate-invite/`, {
        params: { token },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Network error" };
    }
  }



  const completeInviteAPI = async ({ token, name, password }) => {
    try {
      const response = await API.post(`/auth/admin/complete-invite/`, {
        token,
        name,
        password,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Network error" };
    }
  }

  const changePasswordAPI = async (payload) => {
    setLoading(true);
    setError(null);
    try {
      const res = await API.post("/auth/admin/change-password", payload);
      const data = _extract(res);

      if (data.status === "failed") {
        setError(data.message);
        showNotification({
          type: "failed",
          title: "Update Failed",
          message: data.message,
        });
        return data;
      }

      showNotification({
        type: "success",
        title: "Success",
        message: "Password changed successfully!",
      });

      // Update local state to reflect the password change
      const currentAdmin = JSON.parse(localStorage.getItem("admin"));
      if (currentAdmin) {
        const updatedAdmin = { ...currentAdmin, has_changed_password: true };
        localStorage.setItem("admin", JSON.stringify(updatedAdmin));
        setAdmin(updatedAdmin);
      }

      return data;
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to change password.";
      setError(msg);
      showNotification({
        type: "failed",
        title: "Update Failed",
        message: msg,
      });
      return { status: "failed", message: msg };
    } finally {
      setLoading(false);
    }
  };



  const forgotPasswordAPI = async (email) => {
    setLoading(true);
    setError(null);
    try {
      const res = await API.post("/auth/admin/forgot-password", { email });
      const data = _extract(res);
      return data;
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to request code.";
      setError(msg);
      return { status: "failed", message: msg };
    } finally {
      setLoading(false);
    }
  };

  const verifyOtpAPI = async (email, otp) => {
    setLoading(true);
    setError(null);
    try {
      const res = await API.post("/auth/admin/verify-otp", { email, otp });
      const data = _extract(res);
      return data;
    } catch (err) {
      const msg = err.response?.data?.message || "Invalid or expired code.";
      setError(msg);
      return { status: "failed", message: msg };
    } finally {
      setLoading(false);
    }
  };

  const resetPasswordAPI = async (email, token, password) => {
    setLoading(true);
    setError(null);
    try {
      const res = await API.post("/auth/admin/reset-password", { email, token, password });
      const data = _extract(res);
      return data;
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to reset password.";
      setError(msg);
      return { status: "failed", message: msg };
    } finally {
      setLoading(false);
    }
  };


  const clearError = () => setError(null);

  return {
    admin,
    setAdmin,
    loading,
    error,
    adminLogin,
    adminLogout,
    clearError,
    validateInviteAPI,
    completeInviteAPI,
    changePasswordAPI,
    forgotPasswordAPI,
    verifyOtpAPI,
    resetPasswordAPI
  };
}
